// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { SchemaModel } from '../models/Schema'
import { VirtualCollectionAdapter } from './VirtualCollectionAdapter'
import type { RecordCollection, SchemaCollection } from './VirtualCollectionAdapter'
import { EncryptedCollectionAdapter } from './EncryptedCollectionAdapter'
import type { KeyRing, EncryptionContext, KeyLookupFn } from '@epicdm/flowstate-crypto'

/**
 * Manages schema definitions and provides VirtualCollectionAdapters.
 * Central routing point for the schema-in-db system.
 *
 * When encryption is configured via {@link setEncryptionContext}, callers
 * can use {@link getEncryptedAdapter} / {@link getEncryptedAdapterLazy}
 * to get adapters that transparently encrypt/decrypt fields.
 * The base {@link getAdapter} / {@link getAdapterLazy} always return
 * plain VCA instances for callers that need VCA-specific internals
 * (reactive queries, selector rewriting, etc.).
 */
/**
 * Per-(user, org) encryption configuration. Stored in a Map keyed by
 * `${userId}:${orgId}` so a single registry instance can serve many
 * unlocked sessions concurrently — multi-org service accounts and
 * users with concurrent unlocked sessions in different orgs each get
 * their own keyRing/context/lookupKey without overwriting one
 * another. Surfaced during Phase 8 acceptance: a singleton
 * encryption context here meant that unlocking org B silently
 * overwrote org A's crypto for every encrypted CRUD call, even
 * after vault-state itself was correctly per-(user, org).
 */
interface EncryptionEntry {
  keyRing: KeyRing
  context: EncryptionContext
  lookupKey?: KeyLookupFn
}

function encryptionContextKey(userId: string, orgId: string): string {
  if (!userId) throw new Error('SchemaRegistry: userId is required for encryption context')
  if (!orgId) throw new Error('SchemaRegistry: orgId is required for encryption context')
  return `${userId}:${orgId}`
}

function encryptedAdapterCacheKey(userId: string, orgId: string, name: string): string {
  return `${userId}:${orgId}:${name}`
}

export class SchemaRegistry {
  private schemas = new Map<string, SchemaModel>()
  private adapterCache = new Map<string, VirtualCollectionAdapter>()
  /**
   * Encrypted-adapter cache keyed by `${userId}:${orgId}:${name}` so
   * adapters created under one org's keyRing are NOT served to
   * another org's request. The plain (`adapterCache` above) is org-
   * agnostic because the underlying VCA reads the records table
   * directly with no encryption involved.
   */
  private encryptedAdapterCache = new Map<string, EncryptedCollectionAdapter>()

  /**
   * Per-(user, org) encryption entries. Set via
   * `setEncryptionContext(userId, orgId, ...)` after a successful
   * vault-unlock; cleared via `clearEncryptionContext(userId, orgId)`
   * on vault-lock or session end. Concurrent unlocked sessions in
   * different orgs each have their own entry.
   */
  private encryptionContexts = new Map<string, EncryptionEntry>()

  constructor(
    private schemasCollection: SchemaCollection | null,
    private recordsCollection: RecordCollection | null
  ) {}

  /**
   * Load all schema definitions for an org from the database.
   * Uses query-then-swap: existing cache is preserved if the DB query fails.
   *
   * @param orgId - The organization ID to filter schemas by
   */
  async loadSchemas(orgId: string): Promise<void> {
    if (!this.schemasCollection) return
    const docs = await this.schemasCollection
      .find({
        selector: { orgId, archived: false },
      })
      .exec()

    // Query succeeded — build new maps, then swap atomically
    const newSchemas = new Map<string, SchemaModel>()
    for (const doc of docs) {
      const schema = SchemaRegistry.docToSchema(doc)
      newSchemas.set(schema.name, schema)
    }
    if (this.schemas.size > 0 && newSchemas.size === 0) {
      console.warn(
        `[SchemaRegistry] loadSchemas returned 0 results but cache had ${this.schemas.size} schema(s) — replacing anyway`
      )
    }
    this.schemas = newSchemas
    this.adapterCache = new Map()
    this.encryptedAdapterCache = new Map()
  }

  /**
   * Load all non-archived schema definitions across all orgs.
   * Uses query-then-swap: existing cache is preserved if the DB query fails.
   */
  async loadAllSchemas(): Promise<void> {
    if (!this.schemasCollection) return
    const docs = await this.schemasCollection
      .find({
        selector: { archived: false },
      })
      .exec()

    const newSchemas = new Map<string, SchemaModel>()
    for (const doc of docs) {
      const schema = SchemaRegistry.docToSchema(doc)
      newSchemas.set(schema.name, schema)
    }
    if (this.schemas.size > 0 && newSchemas.size === 0) {
      console.warn(
        `[SchemaRegistry] loadAllSchemas returned 0 results but cache had ${this.schemas.size} schema(s) — replacing anyway`
      )
    }
    this.schemas = newSchemas
    this.adapterCache = new Map()
    this.encryptedAdapterCache = new Map()
  }

  /**
   * Lazy single-schema resolution with DB fallback.
   * Checks the in-memory cache first; on miss, queries the DB and caches the result.
   *
   * @param name - Schema name
   * @param orgId - Organization ID for the DB query
   * @returns The schema if found, undefined otherwise
   */
  async getSchemaLazy(name: string, orgId: string): Promise<SchemaModel | undefined> {
    const cached = this.schemas.get(name)
    if (cached) return cached

    if (!this.schemasCollection) return undefined

    const doc = await this.schemasCollection
      .findOne({ selector: { name, orgId, archived: false } })
      .exec()

    if (!doc) return undefined

    const schema = SchemaRegistry.docToSchema(doc)
    this.schemas.set(schema.name, schema)
    return schema
  }

  /**
   * Lazy adapter resolution with DB fallback.
   * Checks adapter cache, then schema cache, then falls back to DB query.
   *
   * @param name - Schema/virtual collection name
   * @param orgId - Organization ID for the DB query
   * @returns A VirtualCollectionAdapter if the schema targets 'records', undefined otherwise
   */
  async getAdapterLazy(name: string, orgId: string): Promise<VirtualCollectionAdapter | undefined> {
    const cached = this.adapterCache.get(name)
    if (cached) return cached

    const schema = this.schemas.get(name) ?? await this.getSchemaLazy(name, orgId)
    if (!schema || schema.target !== 'records') return undefined
    if (!this.recordsCollection) return undefined

    const adapter = new VirtualCollectionAdapter(this.recordsCollection, schema)
    this.adapterCache.set(name, adapter)
    return adapter
  }

  /**
   * Lazy encrypted adapter resolution with DB fallback.
   * Returns an {@link EncryptedCollectionAdapter} when a KeyRing is configured,
   * otherwise returns a plain {@link VirtualCollectionAdapter}.
   *
   * Use this when you need transparent field-level encryption on CRUD operations.
   * Use {@link getAdapterLazy} when you need VCA-specific internals (reactive queries,
   * selector rewriting, etc.).
   *
   * @param name - Schema/virtual collection name
   * @param orgId - Organization ID for the DB query
   */
  async getEncryptedAdapterLazy(
    name: string,
    userId: string,
    orgId: string
  ): Promise<VirtualCollectionAdapter | EncryptedCollectionAdapter | undefined> {
    const cacheKey = encryptedAdapterCacheKey(userId, orgId, name)
    const cached = this.encryptedAdapterCache.get(cacheKey)
    if (cached) return cached

    const vca = await this.getAdapterLazy(name, orgId)
    if (!vca) return undefined

    return this.wrapWithEncryption(userId, orgId, name, vca)
  }

  /** Register a schema directly (for seed/testing without DB) */
  registerSchema(schema: SchemaModel): void {
    this.schemas.set(schema.name, schema)
    this.adapterCache.delete(schema.name)
    this.encryptedAdapterCache.delete(schema.name)
  }

  /** Check if a name matches a registered schema */
  hasSchema(name: string): boolean {
    return this.schemas.has(name)
  }

  /** Get the schema definition by name */
  getSchema(name: string): SchemaModel | undefined {
    return this.schemas.get(name)
  }

  /** Get all registered schema names */
  getSchemaNames(): string[] {
    return Array.from(this.schemas.keys()).sort()
  }

  /** Get all registered schemas */
  getAllSchemas(): SchemaModel[] {
    return Array.from(this.schemas.values())
  }

  /** Get or create a VirtualCollectionAdapter for a schema name */
  getAdapter(name: string): VirtualCollectionAdapter | undefined {
    const cached = this.adapterCache.get(name)
    if (cached) return cached

    const schema = this.schemas.get(name)
    if (!schema || schema.target !== 'records') return undefined
    if (!this.recordsCollection) return undefined

    const adapter = new VirtualCollectionAdapter(this.recordsCollection, schema)
    this.adapterCache.set(name, adapter)
    return adapter
  }

  /**
   * Get or create an encrypted adapter for a schema name.
   * Returns an {@link EncryptedCollectionAdapter} when a KeyRing is configured,
   * otherwise returns a plain {@link VirtualCollectionAdapter}.
   *
   * Use this when you need transparent field-level encryption on CRUD operations.
   * Use {@link getAdapter} when you need VCA-specific internals (reactive queries,
   * selector rewriting, etc.).
   */
  getEncryptedAdapter(
    name: string,
    userId: string,
    orgId: string
  ): VirtualCollectionAdapter | EncryptedCollectionAdapter | undefined {
    const cacheKey = encryptedAdapterCacheKey(userId, orgId, name)
    const cached = this.encryptedAdapterCache.get(cacheKey)
    if (cached) return cached

    const vca = this.getAdapter(name)
    if (!vca) return undefined

    return this.wrapWithEncryption(userId, orgId, name, vca)
  }

  /** Get schemas that overlay a primitive collection */
  getOverlays(primitiveCollection: string): SchemaModel[] {
    return Array.from(this.schemas.values()).filter(s => s.target === primitiveCollection)
  }

  /** Clear all cached state */
  clear(): void {
    this.schemas.clear()
    this.adapterCache.clear()
    this.encryptedAdapterCache.clear()
    this.encryptionContexts.clear()
  }

  /**
   * Configure field-level encryption for the (user, org) session.
   *
   * Multi-org callers (service accounts spanning orgs, users with
   * concurrent vault-unlocks in different orgs) each get their own
   * encryption entry. Setting context for (userA, orgB) does NOT
   * disturb (userA, orgA) — earlier sessions stay unlocked.
   *
   * Invalidates only the (userId, orgId)-scoped slice of the
   * encrypted-adapter cache so other unlocked sessions keep their
   * cached encrypted adapters.
   *
   * @param userId  - Caller's verified userId (from JWT sub/userId claim)
   * @param orgId   - Caller's verified orgId (from JWT orgId claim)
   * @param keyRing - The KeyRing used for encrypt/decrypt operations
   * @param context - Encryption context (orgId, workspaceId, userId)
   * @param lookupKey - Optional key lookup function for group key resolution
   */
  setEncryptionContext(
    userId: string,
    orgId: string,
    keyRing: KeyRing,
    context: EncryptionContext,
    lookupKey?: KeyLookupFn
  ): void {
    const ctxKey = encryptionContextKey(userId, orgId)
    const entry: EncryptionEntry = lookupKey
      ? { keyRing, context, lookupKey }
      : { keyRing, context }
    this.encryptionContexts.set(ctxKey, entry)
    // Invalidate only this session's encrypted-adapter cache so a
    // pending re-lock followed by a fresh unlock rebuilds adapters
    // against the new keyRing — other (user, org) sessions keep
    // their cached adapters.
    const prefix = `${ctxKey}:`
    for (const cacheKey of this.encryptedAdapterCache.keys()) {
      if (cacheKey.startsWith(prefix)) this.encryptedAdapterCache.delete(cacheKey)
    }
  }

  /**
   * Clear the (user, org) encryption entry — encrypted adapters for
   * THIS session resolve to plain VCA again. Other unlocked
   * sessions are unaffected.
   */
  clearEncryptionContext(userId: string, orgId: string): void {
    const ctxKey = encryptionContextKey(userId, orgId)
    this.encryptionContexts.delete(ctxKey)
    const prefix = `${ctxKey}:`
    for (const cacheKey of this.encryptedAdapterCache.keys()) {
      if (cacheKey.startsWith(prefix)) this.encryptedAdapterCache.delete(cacheKey)
    }
  }

  /**
   * Drop ALL encryption contexts and cached encrypted adapters.
   * Called from `MCPServer.close()` during shutdown so key material
   * doesn't outlive the process.
   */
  clearAllEncryptionContexts(): void {
    this.encryptionContexts.clear()
    this.encryptedAdapterCache.clear()
  }

  /**
   * Optionally wrap a VCA with encryption if KeyRing is configured.
   * Caches the result in the encrypted adapter cache.
   */
  private wrapWithEncryption(
    userId: string,
    orgId: string,
    name: string,
    vca: VirtualCollectionAdapter
  ): VirtualCollectionAdapter | EncryptedCollectionAdapter {
    const ctxKey = encryptionContextKey(userId, orgId)
    const entry = this.encryptionContexts.get(ctxKey)
    if (entry) {
      const schema = this.schemas.get(name)
      if (schema) {
        const encrypted = new EncryptedCollectionAdapter(
          vca,
          schema,
          entry.keyRing,
          entry.context,
          entry.lookupKey
        )
        this.encryptedAdapterCache.set(encryptedAdapterCacheKey(userId, orgId, name), encrypted)
        return encrypted
      }
    }
    return vca
  }

  /** Normalize a DB document (RxDB or REST plain object) to a SchemaModel. */
  private static docToSchema(doc: unknown): SchemaModel {
    const obj = doc as { toJSON?: () => Record<string, unknown> }
    return (typeof obj.toJSON === 'function'
      ? obj.toJSON()
      : doc) as unknown as SchemaModel
  }
}
